// /src/components/CryptoTable.jsx
import React, { useMemo, useState } from 'react';
import { formatCurrency, formatCompactNumber } from '../utils/formatters';
import SparklineChart from './SparklineChart';

/**
 * CryptoTable Component
 * Renders the sortable, clickable, glassmorphism table.
 */
const CryptoTable = ({ coins, onCoinClick }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'market_cap', direction: 'desc' });

  const sortedCoins = useMemo(() => {
    let sortableCoins = [...coins];
    if (sortConfig.key) {
      sortableCoins.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableCoins;
  }, [coins, sortConfig]);

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };
  
  const headers = [
    { key: 'market_cap_rank', label: '#', sortable: true, className: "text-left" },
    { key: 'name', label: 'Token', sortable: true, className: "text-left" },
    { key: 'current_price', label: 'Price', sortable: true, className: "text-right" },
    { key: 'price_change_percentage_24h_in_currency', label: '24h %', sortable: true, className: "text-right" },
    { key: 'price_change_percentage_7d_in_currency', label: '7d %', sortable: true, className: "text-right" },
    { key: 'market_cap', label: 'Market Cap', sortable: true, className: "text-right" },
    { key: 'total_volume', label: 'Volume (24h)', sortable: true, className: "text-right" },
    { key: 'sparkline_in_7d', label: '7d Chart', sortable: false, className: "text-right pr-4" },
  ];

  const PercentChangeCell = ({ value }) => {
    if (value === null || value === undefined) {
      return <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-400">N/A</td>;
    }
    const isPositive = value > 0;
    return (
      <td className={`px-2 py-4 whitespace-nowrap text-sm font-medium text-right ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        <div className="flex items-center justify-end">
          {isPositive ? '▲' : '▼'}
          <span>{value?.toFixed(2)}%</span>
        </div>
      </td>
    );
  };

  return (
    // Apply sticky positioning to the container
    <div className="overflow-x-auto shadow-2xl rounded-lg border border-gray-700/50 sticky top-[106px] z-20">
      <table className="min-w-full divide-y divide-gray-700/50">
        {/* Make sticky to the top of its parent container */}
        <thead className="bg-gray-900 sticky top-0 z-10">
          <tr>
            {headers.map((header) => (
              <th
                key={header.key}
                scope="col"
                className={`px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider transition-colors hover:text-white ${header.className} ${header.sortable ? 'cursor-pointer' : ''}`}
                onClick={() => header.sortable && requestSort(header.key)}
              >
                <div className={`flex items-center ${header.className.includes('right') ? 'justify-end' : 'justify-start'}`}>
                  {header.label}
                  {header.sortable && <span className="ml-1 w-4">{getSortIndicator(header.key)}</span>}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/50 bg-gray-800">
          {sortedCoins.map((coin, index) => (
            <tr 
              key={coin.id} 
              className="hover:bg-gray-700/50 cursor-pointer transition-all duration-150 animate-staggeredFadeIn"
              style={{ animationDelay: `${index * 20}ms` }}
              onClick={() => onCoinClick(coin.id)}
            >
              {/* Rank */}
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400">{coin.market_cap_rank}</td>
              
              {/* Token Name, Logo, Symbol */}
              <td className="px-2 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8">
                    <img className="h-8 w-8 rounded-full" src={coin.image} alt={coin.name} loading="lazy" />
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-white">{coin.name}</div>
                    <div className="text-sm text-gray-400 uppercase">{coin.symbol}</div>
                  </div>
                </div>
              </td>
              
              {/* Price */}
              <td className="px-2 py-4 whitespace-nowrap text-sm text-right font-medium text-white">{formatCurrency(coin.current_price)}</td>
              
              {/* 24h % Change */}
              <PercentChangeCell value={coin.price_change_percentage_24h_in_currency} />
              
              {/* 7d % Change */}
              <PercentChangeCell value={coin.price_change_percentage_7d_in_currency} />
              
              {/* Market Cap */}
              <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-300">{formatCompactNumber(coin.market_cap)}</td>
              
              {/* Volume */}
              <td className="px-2 py-4 whitespace-nowrap text-sm text-right text-gray-300">{formatCompactNumber(coin.total_volume)}</td>
              
              {/* 7d Sparkline */}
              <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                {coin.sparkline_in_7d && coin.sparkline_in_7d.price.length > 0 && (
                  <SparklineChart 
                    data={coin.sparkline_in_7d.price} 
                    strokeColor={coin.price_change_percentage_7d_in_currency > 0 ? '#22c55e' : '#ef4444'} 
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CryptoTable;